/**
 * eventTextMeCopy.ts — every line "💬 Text me about this one" can say. #1966,
 * her item 11.
 *
 * Separate from `eventTextMeReminder.ts` so the three surfaces (the add-event
 * sheet, the ✏️ popover and the import rows) cannot drift into three
 * wordings, and so the copy can be asserted without importing a component.
 *
 * ⚠️ Two rules Kasandra set, and both are about not lying:
 *
 *   1. A FREE account sees the toggle DISABLED with ONE LINE — no upsell
 *      modal. The toggle is visible because hiding it hides the feature; it is
 *      disabled because the `scheduled_tasks` INSERT policy is
 *      `task_type in ('snack_mom','signup_item') or is_current_user_pro()`
 *      and a refused insert returns ZERO ROWS AND NO ERROR. Offering an
 *      enabled control over that is how you get a success toast about a row
 *      that does not exist.
 *   2. A QUIET-HOURS SNAP IS TOLD TO HER. She chose an offset; if the fire
 *      time lands in 21:00–07:00 the row gets moved to the edge of the
 *      deliverable window, and a reminder arriving at a different time than
 *      the one implied by her choice has to be stated, not discovered.
 */

import { formatTime12h } from "./timeFormat";
import type { EventTextMeFailure } from "./eventTextMeReminder";

/** The toggle, in her words. */
export const TEXT_ME_TOGGLE_LABEL = "💬 Text me about this one";

/** The one line under a disabled toggle. No modal, no /pro link — she asked
 *  for a note, and a modal in a save sheet is a dead end mid-task. */
export const TEXT_ME_PRO_NOTE = "Texts about your events are part of Kin Pro.";

/** The helper under an ENABLED toggle. Says the channel, because the calendar
 *  reminder sitting next to it says "Google or Apple Calendar" and the whole
 *  point of this one is that KIN does the reminding. */
export const TEXT_ME_HELPER = "Kin texts you before it starts 💜";

/**
 * The one line under the toggle when Kin has no way to text her.
 *
 * ⚠️ Copilot on #1983: Pro gates WRITING the row and says nothing about
 * delivery. `send-sms` skips any account with no verified phone or with STOP
 * set, so without this she could be told "Kin will text you at 1:00 PM" about
 * a text that can never be sent. Points at the fix rather than just refusing.
 */
export const TEXT_ME_NEEDS_PHONE_NOTE =
  "Add and verify your phone in Settings and Kin can text you about this.";

/** Shown when the write landed nowhere and she is not Pro. */
export const TEXT_ME_PRO_REFUSED_TOAST =
  "Texts about your events are part of Kin Pro — nothing was scheduled.";

/** Shown when the write landed nowhere for any other reason. */
export const TEXT_ME_SAVE_FAILED_TOAST = "Couldn't set that text up. Try again?";

/**
 * Which refusal to show for a write that returned no row.
 *
 * ⚠️ `proBlocked`, not `!isPro`: `proBlocked = proLoaded && !isProTier` is
 * FALSE while entitlements load (#616, deliberate), so an unknown tier must
 * get the generic line. Blaming the tier for a network blip sends her to the
 * upgrade page over nothing.
 */
export function textMeRefusedToast(proBlocked: boolean): string {
  return proBlocked ? TEXT_ME_PRO_REFUSED_TOAST : TEXT_ME_SAVE_FAILED_TOAST;
}

/**
 * What to say when the reminder could not be placed at all.
 *
 * ⚠️ Every one of these is a REFUSAL, so each has to name the thing she can
 * change. "Couldn't do that" on a toggle she just flipped is the shape that
 * makes a feature feel broken rather than constrained.
 */
export function textMeUnavailableNote(reason: EventTextMeFailure): string {
  switch (reason) {
    case "no_timezone":
      // The runner gates BOTH channels on notifications_timezone and fails
      // closed without it, so this is not pedantry — the row could not fire.
      return "Kin needs your timezone first — open Settings once and it fills itself in.";
    case "in_past":
      return "That's already passed, so there's no time left to text you.";
    case "no_slot":
      // The honest version of "quiet hours ate it". Happens when snapping
      // forward to 07:00 would land after the event itself.
      return "Kin's texts are quiet 9pm–7am, and there's no slot left before this starts.";
    case "bad_date":
    case "bad_time":
    default:
      return "Kin couldn't work out when to text you about this one.";
  }
}

/**
 * The line she sees when the slot had to move out of quiet hours.
 *
 * Names the real time rather than the offset she picked, because the offset is
 * now wrong and the time is what will actually happen.
 */
export function textMeSnappedNote(localTime: string): string {
  return `Quiet hours — Kin will text you at ${formatTime12h(localTime)} instead.`;
}

/** The success toast. `localTime` is the row's own wall clock. */
export function textMeSavedToast(localTime: string, snapped: boolean): string {
  const when = formatTime12h(localTime);
  return snapped
    ? `Kin will text you at ${when} — quiet hours moved it 💜`
    : `Kin will text you at ${when} 💜`;
}

/** The Tasks-tab card label: "💬 Text me · Sloane's party · Sat 2:00 PM". */
export function textMeCardLabel(eventTitle: string, whenLabel: string): string {
  return `💬 Text me · ${eventTitle} · ${whenLabel}`;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * "Sat 2:00 PM" from the row's own `local_date` + `local_time`.
 *
 * ⚠️ Built with Date.UTC and read with getUTCDay, NOT `new Date("2026-11-14")`
 * plus getDay. That constructor parses a bare date as UTC midnight, so west of
 * Greenwich `getDay()` returns the PREVIOUS day and the card would name the
 * wrong weekday for every reminder — invisibly, because it is still a
 * plausible weekday.
 *
 * ⚠️ And this is the time KIN TEXTS HER, which is the row's own slot. Not the
 * event's start time: the task row does not carry it (only `config.local_*`,
 * which the trigger maintains as the fire time), and inventing it from the
 * offset would be a second place for the two to disagree.
 */
export function textMeWhenLabel(localDate?: string | null, localTime?: string | null): string {
  if (typeof localDate !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(localDate)) {
    return formatTime12h(localTime);
  }
  const [y, m, d] = localDate.split("-").map((n) => parseInt(n, 10));
  const probe = new Date(Date.UTC(y, m - 1, d));
  // Date.UTC normalises an impossible date (2026-02-31 -> March 3rd), so
  // round-trip it rather than naming a day that does not exist.
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) {
    return formatTime12h(localTime);
  }
  const clock = formatTime12h(localTime);
  return `${WEEKDAYS[probe.getUTCDay()]} ${clock}`;
}

/** Whether a scheduled task is one of these event-linked texts. */
export function isEventTextMeTask(task: {
  task_type?: string | null;
  event_id?: string | null;
}): boolean {
  // ⚠️ The COLUMN, not `config.event_id`. The column is what the FK and the
  // partial index are on, and what the trigger matches; a row carrying the id
  // only in config is one the trigger will never touch.
  return task?.task_type === "timed_reminder" && typeof task?.event_id === "string" && !!task.event_id;
}

/**
 * The composed Tasks-tab label for an event-linked text, or null when the row
 * is not one — so the caller falls through to `display_name` rather than
 * rendering an empty title.
 */
export function eventTextMeTaskLabel(task: {
  task_type?: string | null;
  event_id?: string | null;
  display_name?: string | null;
  schedule_time?: string | null;
  config?: unknown;
}): string | null {
  if (!isEventTextMeTask(task)) return null;
  const config = task.config && typeof task.config === "object" ? (task.config as Record<string, unknown>) : {};
  const title =
    typeof config.event_title === "string" && config.event_title.trim()
      ? config.event_title.trim()
      : (task.display_name || "your event");
  const when = textMeWhenLabel(
    typeof config.local_date === "string" ? config.local_date : null,
    typeof config.local_time === "string" ? config.local_time : task.schedule_time,
  );
  return textMeCardLabel(title, when);
}
